import React from 'react'
import { Link } from 'react-router-dom'
import { Box, Text, Button, Group } from '@mantine/core'

interface Props {
  /** The gated action being described, e.g. "track your collection" */
  action?: string;
  /** Short pitch copy below the heading. Defaults to a sensible generic. */
  pitch?: string;
  /** Bullet points showing value. 2–3 max. */
  bullets?: string[];
  /** Visual variant: "banner" = horizontal strip, "card" = centred block, "inline" = compact single line */
  variant?: 'banner' | 'card' | 'inline';
}

const DEFAULT_BULLETS = [
  'Track every firearm in your personal Gun Vault',
  'Document builds, log parts, and follow other collectors',
  'Earn AmmoScore by contributing to the community Registry'
]

export default function AuthGateCta ({
  action = 'access this feature',
  pitch,
  bullets,
  variant = 'card'
}: Props) {
  const pts = bullets ?? DEFAULT_BULLETS
  const pitchText = pitch ?? `Create a free account to ${action}.`

  if (variant === 'inline') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          background: 'rgba(232,93,4,0.06)',
          border: '1px solid rgba(232,93,4,0.2)',
          borderRadius: 6,
          padding: '10px 16px',
          flexWrap: 'wrap'
        }}
      >
        <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-text)' }}>
          {pitchText}
        </Text>
        <Group spacing="xs">
          <Button
            component={Link}
            to="/register"
            size="xs"
            sx={{
              background: '#E85D04',
              color: '#fff',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 700,
              '&:hover': { background: '#cf4f00' }
            }}
          >
            Create Free Account
          </Button>
          <Button
            component={Link}
            to="/login"
            size="xs"
            variant="default"
            sx={{
              background: 'transparent',
              border: '1px solid var(--gs-border-bright)',
              color: 'var(--gs-text)',
              fontFamily: 'DM Sans, sans-serif',
              '&:hover': { borderColor: 'var(--gs-accent)' }
            }}
          >
            Sign In
          </Button>
        </Group>
      </Box>
    )
  }

  if (variant === 'banner') {
    return (
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(232,93,4,0.08) 0%, rgba(201,168,76,0.06) 100%)',
          border: '1px solid rgba(232,93,4,0.22)',
          borderRadius: 8,
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
          marginBottom: 20
        }}
      >
        <Box>
          <Text
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--gs-text)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 4
            }}
          >
            Join the GunSlop Community
          </Text>
          <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-muted)' }}>
            {pitchText}
          </Text>
        </Box>
        <Group spacing="xs" sx={{ flexShrink: 0 }}>
          <Button
            component={Link}
            to="/register"
            size="sm"
            sx={{
              background: '#E85D04',
              color: '#fff',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 700,
              '&:hover': { background: '#cf4f00' }
            }}
          >
            Create Free Account
          </Button>
          <Button
            component={Link}
            to="/login"
            size="sm"
            variant="default"
            sx={{
              background: 'transparent',
              border: '1px solid var(--gs-border-bright)',
              color: 'var(--gs-text)',
              fontFamily: 'DM Sans, sans-serif',
              '&:hover': { borderColor: 'var(--gs-accent)' }
            }}
          >
            Sign In
          </Button>
        </Group>
      </Box>
    )
  }

  // "card" variant — centred, with bullets
  return (
    <Box
      sx={{
        textAlign: 'center',
        padding: '48px 24px',
        maxWidth: 480,
        margin: '0 auto'
      }}
    >
      {/* Logo mark */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(232,93,4,0.12)',
          border: '1.5px solid rgba(232,93,4,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
          fontSize: 24
        }}
      >
        🔒
      </Box>

      <Text
        sx={{
          fontFamily: 'Oswald, sans-serif',
          fontSize: 22,
          fontWeight: 700,
          color: 'var(--gs-text)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: 8
        }}
      >
        Create Your Free Account
      </Text>

      <Text
        sx={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 14,
          color: 'var(--gs-muted)',
          marginBottom: 24,
          lineHeight: 1.5
        }}
      >
        {pitchText}
      </Text>

      {/* Bullets */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 28,
          textAlign: 'left',
          background: 'var(--gs-surface2)',
          border: '1px solid var(--gs-border)',
          borderRadius: 8,
          padding: '14px 18px'
        }}
      >
        {pts.map((b, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <Text sx={{ color: '#E85D04', fontSize: 13, lineHeight: 1.5, flexShrink: 0 }}>✓</Text>
            <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-text)', lineHeight: 1.5 }}>
              {b}
            </Text>
          </Box>
        ))}
      </Box>

      <Button
        component={Link}
        to="/register"
        fullWidth
        size="md"
        sx={{
          background: '#E85D04',
          color: '#fff',
          fontFamily: 'Oswald, sans-serif',
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: '0.5px',
          marginBottom: 10,
          '&:hover': { background: '#cf4f00' }
        }}
      >
        Create Free Account
      </Button>
      <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)' }}>
        Already a member?{' '}
        <Text
          component={Link}
          to="/login"
          sx={{
            color: 'var(--gs-accent)',
            textDecoration: 'none',
            fontWeight: 600,
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          Sign in
        </Text>
      </Text>
    </Box>
  )
}
